import React, { ChangeEvent, FC, useState } from 'react';
import {
  FormControl,
  Grid,
  List,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';

import { Tip } from 'components';
import { LeaderboardConfig, LeaderboardEventScores } from 'api';
import { ScoringList } from '../index';
import {
  ConnectShare,
  LinkedIn,
  InviteColleague,
  RelaunchRefresh,
  Sharing,
  SetToZeroPreset,
  Twitter,
  UserGeneratedContent,
  DefaultPreset,
} from './presets';
import { flattenDeep, values as _values } from 'lodash';

enum Presets {
  Default = 'default',
  SetToZero = 'zero',
  Sharing = 'sharing',
  Twitter = 'twitter',
  LinkedIn = 'linkedin',
  InviteColleague = 'inviteColleague',
  RelaunchRefresh = 'relaunchRefresh',
  ConnectShare = 'ConnectShare',
  UserGeneratedContent = 'UserGeneratedContent',
}

const Scorings: FC = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<LeaderboardConfig>();

  const { scoring_aggregate } = values;

  const [selectOption, setSelectOption] = useState('');

  function getTotal() {
    const events = _values(scoring_aggregate);
    const scores = events.map(event => _values(event));
    return flattenDeep(scores).reduce((acc: number, item: LeaderboardEventScores) => {
      if (item.score !== '') {
        return acc + parseFloat(item.score);
      }
      return acc;
    }, 0);
  }

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    setSelectOption(value as Presets);

    switch (value) {
      case Presets.Default:
        setFieldValue('scoring_aggregate', DefaultPreset);
        break;
      case Presets.SetToZero:
        setFieldValue('scoring_aggregate', SetToZeroPreset);
        break;
      case Presets.Sharing:
        setFieldValue('scoring_aggregate', Sharing);
        break;
      case Presets.Twitter:
        setFieldValue('scoring_aggregate', Twitter);
        break;
      case Presets.LinkedIn:
        setFieldValue('scoring_aggregate', LinkedIn);
        break;
      case Presets.InviteColleague:
        setFieldValue('scoring_aggregate', InviteColleague);
        break;
      case Presets.RelaunchRefresh:
        setFieldValue('scoring_aggregate', RelaunchRefresh);
        break;
      case Presets.ConnectShare:
        setFieldValue('scoring_aggregate', ConnectShare);
        break;
      case Presets.UserGeneratedContent:
        setFieldValue('scoring_aggregate', UserGeneratedContent);
        break;
    }
  }

  function renderLists() {
    return Object.entries(scoring_aggregate).map(([key], idx) => {
      return <ScoringList category={key} key={key + idx} />;
    });
  }

  return (
    <Grid container>
      <Grid item xs={3}>
        <Typography variant="subtitle2">{t('admin:scoring')}</Typography>
      </Grid>

      <Grid item xs={8} container justify="space-between">
        <Grid item xs={8}>
          <Typography variant="subtitle2" gutterBottom>{`${getTotal().toFixed(1)} ${t(
            'common:point_plural'
          )}`}</Typography>
          <Tip i18nKey={t('admin:scoringTip')} disableMargin />
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth>
            <Select fullWidth displayEmpty value={selectOption} onChange={onChange}>
              <MenuItem value="" disabled>
                {t('admin:presets')}
              </MenuItem>
              <MenuItem value={Presets.Default}>{t('admin:restoreToDefault')}</MenuItem>
              <MenuItem value={Presets.SetToZero}>{t('admin:setAllToZero')}</MenuItem>
              <MenuItem value={Presets.Sharing}>{t('admin:sharingFocused')}</MenuItem>
              <MenuItem value={Presets.Twitter}>{t('admin:twitterFocused')}</MenuItem>
              <MenuItem value={Presets.LinkedIn}>{t('admin:linkedinFocused')}</MenuItem>
              <MenuItem value={Presets.InviteColleague}>{t('admin:inviteColleague')}</MenuItem>
              <MenuItem value={Presets.RelaunchRefresh}>{t('admin:relaunchRefresh')}</MenuItem>
              <MenuItem value={Presets.ConnectShare}>{t('admin:connectShare')}</MenuItem>
              <MenuItem value={Presets.UserGeneratedContent}>
                {t('admin:userGeneratedContent')}
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <List
            dense
            disablePadding
            subheader={
              <ListSubheader disableGutters>
                <Grid container alignItems="flex-end">
                  <Grid item xs={9}>
                    <Typography variant="body1">{t('admin:category_plural')}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body1" align="right">
                      {t('common:point_plural')}
                    </Typography>
                  </Grid>
                </Grid>
              </ListSubheader>
            }
          >
            {renderLists()}
          </List>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Scorings;
